/**
 * useFunnel Hook - Access funnel state and navigation methods
 * 
 * This hook consumes the funnel state from TagadaProvider. All complex 
 * initialization and session management is handled at the provider level.
 * 
 * Usage:
 * ```tsx
 * // In your root component or layout:
 * <TagadaProvider funnelId="funnelv2_xxx" autoInitializeFunnel={true}>
 *   <YourApp />
 * </TagadaProvider>
 * 
 * // In any child component:
 * const { context, next, isLoading, stepConfig } = useFunnel();
 * 
 * // Access step-specific config (payment flows, resources, etc.)
 * const offerId = stepConfig.resources?.offer;
 * const paymentFlowId = stepConfig.paymentFlowId;
 * ```
 */

import { useMemo } from 'react';
import {
    FunnelState,
    GTMTrackingConfig,
    PixelTrackingConfig,
    RuntimeStepConfig,
    SnapchatTrackingConfig,
    TrackingProvider,
    getAssignedOrderBumpOfferIds,
    getAssignedPaymentFlowId,
    getAssignedPixels,
    getAssignedResources,
    getAssignedScripts,
    getAssignedStepConfig,
    getAssignedUpsellOfferIds,
} from '../../core/funnelClient';
import { FunnelAction, FunnelNavigationResult, SimpleFunnelContext } from '../../core/resources/funnel';
import { useTagadaContext } from '../providers/TagadaProvider';

/**
 * Step configuration from HTML injection (for current step/variant)
 */
export interface StepConfigValue {
    /**
     * Full step configuration object
     */
    raw: RuntimeStepConfig | undefined;

    /**
     * Payment flow ID override for this step
     * If set, this payment flow should be used instead of the store default
     */
    paymentFlowId: string | undefined;

    /**
     * Resource bindings for this step/variant.
     * e.g., { offer: 'offer_xxx', product: 'product_xxx' }
     */
    resources: Record<string, string> | undefined;

    /** @deprecated Use `resources` instead */
    staticResources: Record<string, string> | undefined;

    /**
     * Get scripts for a specific injection position
     * Only returns enabled scripts
     */
    getScripts: (position?: 'head-start' | 'head-end' | 'body-start' | 'body-end') => RuntimeStepConfig['scripts'];

    /**
     * Pixel tracking configuration
     */
    pixels?: {
        [TrackingProvider.FACEBOOK]?: PixelTrackingConfig[];
        [TrackingProvider.TIKTOK]?: PixelTrackingConfig[];
        [TrackingProvider.SNAPCHAT]?: SnapchatTrackingConfig[];
        [TrackingProvider.GTM]?: GTMTrackingConfig[];
    };

    /**
     * Enabled order bump offer IDs for this step.
     * undefined = inherit all store bumps, string[] = only these IDs.
     */
    orderBumpOfferIds: string[] | undefined;

    /**
     * Enabled upsell offer IDs for this step.
     * undefined = inherit all store upsells, string[] = only these IDs.
     */
    upsellOfferIds: string[] | undefined;
}

export interface FunnelContextValue extends FunnelState {
    // Current step helper
    currentStep: { id: string } | null;

    // Step configuration (from HTML injection)
    stepConfig: StepConfigValue;

    // Methods
    next: (event: FunnelAction, options?: { waitForSession?: boolean }) => Promise<FunnelNavigationResult>;
    goToStep: (stepId: string) => Promise<FunnelNavigationResult>;
    updateContext: (updates: Partial<SimpleFunnelContext>) => Promise<void>;
    initializeSession: (entryStepId?: string) => Promise<void>;
    endSession: () => Promise<void>;
    retryInitialization: () => Promise<void>;
    refetch: () => Promise<void>;
}

/**
 * Hook to access funnel state and methods
 * 
 * This hook returns the funnel state from TagadaProvider plus step configuration
 * from HTML injection. All complex logic is handled at the provider level.
 * 
 * @returns FunnelContextValue with state, methods, and step config
 */
export function useFunnel(): FunnelContextValue {
    const { funnel } = useTagadaContext();

    // Compute step config from HTML injection (memoized, computed once on mount)
    const stepConfig = useMemo<StepConfigValue>(() => {
        const raw = getAssignedStepConfig();
        const resources = getAssignedResources();
        return {
            raw,
            paymentFlowId: getAssignedPaymentFlowId(),
            resources,
            staticResources: resources,
            pixels: getAssignedPixels(),
            getScripts: (position) => getAssignedScripts(position),
            orderBumpOfferIds: getAssignedOrderBumpOfferIds(),
            upsellOfferIds: getAssignedUpsellOfferIds(),
        };
    }, []);

    return {
        ...funnel,
        stepConfig,
    };
}

